<template>
  <div>
    <div class="choice_map">
      <button
        :class="currentBtn === 1 ? 'on' : ''"
        class="map_btn"
        @click="detail(1)"
      >
        위성
      </button>
      <button
        :class="currentBtn === 2 ? 'on' : ''"
        class="map_btn"
        @click="detail(2)"
      >
        일반
      </button>
    </div>
    <div class="choice_map second">
      <button
        v-for="(item, index) in bookmarks"
        :key="index"
        class="map_btn"
        @click="move(item)"
      >
        <span v-if="item.orderId !== 0">{{ item.orderId }}</span>
        {{ item.name }}
      </button>
    </div>
    <div class="rtk-panel-wrap">
      <div class="rtk-panel">
        <div class="third-column date-column">
          <p class="section-title">날짜 선택</p>
          <date-picker
            v-model="param.startDate"
            :lang="getLang"
            value-type="format"
            format="YYYY-MM-DD"
            type="date"
            @change="changeDate"
          />
        </div>
        <div class="third-column equipment-column">
          <div class="section-header">
            <p class="section-title">운행중인 장비</p>
            <button
              class="map_btn refresh_btn"
              :disabled="rtkLoading"
              @click="fetchRunningEquipments"
            >
              {{ rtkLoading ? "..." : "↻" }}
            </button>
          </div>
          <div class="equipment-list">
            <div v-if="rtkLoading" class="placeholder">
              데이터를 불러오는 중입니다.
            </div>
            <div v-else-if="rtkError" class="placeholder error">
              {{ rtkError }}
            </div>
            <div v-else-if="!runningEquipments.length" class="placeholder">
              운행중인 장비가 없습니다.
            </div>
            <template v-else>
              <button
                v-for="(item, index) in runningEquipments"
                :key="item.deviceId || index"
                :class="[
                  'map_btn',
                  'equipment_btn',
                  { 'equipment-no-today': !hasTodayRecord(item) },
                  { tracking: trackedDeviceId === item.deviceId },
                ]"
                @click="trackEquipment(item)"
              >
                <div class="equipment-main">
                  <span class="equipment-name">
                    {{ item.equipmentName || item.serialNumber || "장비" }}
                  </span>
                  <div
                    v-if="item.carNo || !hasTodayRecord(item)"
                    class="equipment-meta"
                  >
                    <span v-if="!hasTodayRecord(item)" class="no-today-badge">
                      오늘 운행 없음
                    </span>
                  </div>
                </div>
                <div class="equipment-stats">
                  <div class="stat-row">
                    <span class="stat-label">오늘</span>
                    <span class="stat-value">{{
                      formatDistance(item.todayDistance)
                    }}</span>
                  </div>
                  <div class="stat-row">
                    <span class="stat-label">월간</span>
                    <span class="stat-value">{{
                      formatDistance(item.monthDistance)
                    }}</span>
                  </div>
                  <div class="stat-row">
                    <span class="stat-label">연간</span>
                    <span class="stat-value">{{
                      formatDistance(item.yearDistance)
                    }}</span>
                  </div>
                </div>
                <div class="equipment-speed-row">
                  <span>최고속도: {{ formatSpeed(item.todayMaxSpeed) }}</span>
                </div>
                <div class="equipment-actions">
                  <button
                    class="route-btn"
                    :class="{ active: activeRouteDeviceId === item.deviceId }"
                    @click.stop="toggleRoute(item)"
                  >
                    {{
                      activeRouteDeviceId === item.deviceId
                        ? "경로숨기기"
                        : "경로찾기"
                    }}
                  </button>
                  <div
                    v-if="activeRouteDeviceId === item.deviceId"
                    class="playback-controls"
                  >
                    <button
                      class="playback-btn"
                      :class="{ active: routePlaying }"
                      @click.stop="togglePlayPause"
                      :title="routePlaying ? '일시정지' : '재생'"
                    >
                      <span v-if="!routePlaying">▶</span>
                      <span v-else>⏸</span>
                    </button>
                    <button
                      class="playback-btn"
                      @click.stop="stopPlayback"
                      title="정지"
                    >
                      ⏹
                    </button>
                  </div>
                </div>
              </button>
            </template>
          </div>
        </div>
      </div>
    </div>
    <popup-calendar ref="calendar" @calendarCallBack="calendarCallBack" />
  </div>
</template>
<script>
import "vue2-datepicker/index.css";
import DatePicker from "vue2-datepicker";
import CustomObjectApi from "../../api/osm/CustomObjectApi";
import PopupCalendar from "../popup/PopupCalendar.vue";
import RtkApi from "../../api/osm/RtkApi";
import { getToday } from "../../utils/date";
import { mapGetters } from "vuex";

export default {
  name: "ChoiceMap",
  components: { PopupCalendar, DatePicker },
  props: {
    deviceInfo: { type: Object, default: () => null },
    routePlaying: { type: Boolean, default: false },
  },
  data: () => {
    return {
      param: {
        startDate: getToday(),
      },
      currentBtn: 1,
      bookmarks: [],
      runningEquipments: [],
      rtkLoading: false,
      rtkError: null,
      logType: "실시간",
      rtkRealtimeInterval: null,
      rtkSummaryInterval: null,
      activeRouteDeviceId: null,
      routeLoading: false,
      trackedDeviceId: null, // Currently tracked equipment
    };
  },
  computed: { ...mapGetters(["getLang"]) },
  created() {},
  async mounted() {
    this.bookmarks = await CustomObjectApi.bookmarks();
    await this.fetchRunningEquipments();

    // 실시간 위치는 3초, 운행 요약은 60초 주기로 갱신
    this.startPolling();
  },
  beforeDestroy() {
    this.stopPolling();
  },
  methods: {
    detail(item) {
      if (item === 1) {
        if (this.currentBtn !== 1) {
          this.currentBtn = item;
          this.$emit("changeMapStyle", "satellite-v9");
        }
      } else {
        if (this.currentBtn !== 2) {
          this.currentBtn = item;
          this.$emit("changeMapStyle", "default");
        }
      }
    },
    move(item) {
      this.$emit("moveBookmarks", item);
    },
    async fetchRunningEquipments() {
      try {
        this.rtkLoading = true;
        this.rtkError = null;
        const response = await RtkApi.getEquipmentRunSummary();
        this.runningEquipments = (response && response.content) || [];

        // 실시간 위치도 함께 조회
        await this.fetchRealtimePositions();
      } catch (error) {
        console.error(error);
        this.rtkError = "GNSS 데이터를 불러오지 못했습니다.";
      } finally {
        this.rtkLoading = false;
      }
    },
    async fetchRealtimePositions() {
      try {
        const response = await RtkApi.getRealtimeAll();
        const positions = (response && response.content) || [];
        this.$emit("updateRtkPositions", positions);
      } catch (error) {
        console.error("실시간 위치 조회 실패:", error);
      }
    },
    async fetchEquipmentSummary() {
      try {
        const response = await RtkApi.getEquipmentRunSummary();
        this.runningEquipments = (response && response.content) || [];
      } catch (error) {
        console.error("운행 요약 조회 실패:", error);
      }
    },
    formatDistance(meters) {
      if (meters === null || meters === undefined) return "-";
      if (isNaN(meters)) return "-";
      const km = meters / 1000;
      return `${km.toFixed(1)}km`;
    },
    formatSpeed(speed) {
      if (speed === null || speed === undefined) return "-";
      if (isNaN(speed)) return "-";
      return `${Number(speed).toFixed(1)}km/h`;
    },
    hasTodayRecord(item) {
      if (!item) return false;
      const distance = Number(item.todayDistance ?? 0);
      const maxSpeed = Number(item.todayMaxSpeed ?? 0);
      return distance > 0 || maxSpeed > 0;
    },
    async changeDate() {
      console.log(this.param.startDate);
      this.$emit("changeDate", this.param.startDate);
      await this.fetchRunningEquipments();
    },
    openCalendar(item) {
      console.log("item", item);
      this.$refs.calendar.open(item);
    },
    calendarCallBack(param) {
      this.logType = param.logDay;
      this.$emit("calendarCallBack", param);
    },
    logRefresh() {
      this.logType = "실시간";
      this.$emit("refreshCallBack");
    },
    async toggleRoute(item) {
      if (this.activeRouteDeviceId === item.deviceId) {
        // 경로 숨기기
        this.activeRouteDeviceId = null;
        this.$emit("clearRtkRoute");
        // 폴링 재시작
        this.startPolling();
        return;
      }

      try {
        this.routeLoading = true;
        // 폴링 중지
        this.stopPolling();
        // 선택한 날짜를 시작일과 종료일로 동일하게 설정 (당일 경로)
        const selectedDate = this.param.startDate;
        const response = await RtkApi.getHistoryByDevice(
          item.deviceId,
          selectedDate,
          selectedDate
        );
        const content = response && response.content;
        const routeData = (content && content.histories) || [];

        if (routeData.length > 0) {
          this.activeRouteDeviceId = item.deviceId;
          this.$emit("drawRtkRoute", {
            deviceId: item.deviceId,
            equipmentName:
              content.equipmentName ||
              item.equipmentName ||
              item.serialNumber ||
              "장비",
            route: routeData,
          });
        } else {
          alert("해당 날짜에 경로 데이터가 없습니다.");
          // 경로가 없으면 폴링 재시작
          this.startPolling();
        }
      } catch (error) {
        console.error("경로 조회 실패:", error);
        alert("경로를 불러오지 못했습니다.");
        // 에러 발생 시 폴링 재시작
        this.startPolling();
      } finally {
        this.routeLoading = false;
      }
    },
    startPolling() {
      if (this.rtkRealtimeInterval || this.rtkSummaryInterval) return; // 이미 실행 중이면 무시
      this.fetchRealtimePositions();
      this.fetchEquipmentSummary();
      this.rtkRealtimeInterval = setInterval(() => {
        this.fetchRealtimePositions();
      }, 3000); // 3초마다 실시간 위치 업데이트
      this.rtkSummaryInterval = setInterval(() => {
        this.fetchEquipmentSummary();
      }, 60000); // 60초마다 운행 요약 업데이트
    },
    stopPolling() {
      if (this.rtkRealtimeInterval) {
        clearInterval(this.rtkRealtimeInterval);
        this.rtkRealtimeInterval = null;
      }
      if (this.rtkSummaryInterval) {
        clearInterval(this.rtkSummaryInterval);
        this.rtkSummaryInterval = null;
      }
    },
    togglePlayPause() {
      if (this.routePlaying) {
        this.$emit("pauseRoute");
      } else {
        this.$emit("playRoute");
      }
    },
    stopPlayback() {
      this.$emit("stopRoute");
    },
    trackEquipment(item) {
      if (this.trackedDeviceId === item.deviceId) {
        // Stop tracking if clicking the same device
        this.trackedDeviceId = null;
        this.$emit("stopTracking");
      } else {
        // Start tracking new device
        this.trackedDeviceId = item.deviceId;
        this.$emit("trackEquipment", item.deviceId);
      }
    },
  },
};
</script>
<style scoped>
.rtk-panel-wrap {
  position: fixed;
  top: 80px;
  right: 12px;
  z-index: 100;
}

.rtk-panel {
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 220px;
  background: #1a1f2e;
  border: 1px solid #2d3548;
  border-radius: 12px;
  padding: 14px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.4);
}

.third-column {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.date-column ::v-deep .mx-datepicker {
  width: 100% !important;
  margin: 0 !important;
}

.rtk-panel ::v-deep .mx-input {
  background: #252b3d !important;
  color: #e8ecf4 !important;
  border: 1px solid #3d4559 !important;
  border-radius: 6px !important;
  height: 36px;
  font-size: 13px;
  padding: 8px 10px;
}
.rtk-panel ::v-deep .mx-input-wrapper {
  background-color: transparent !important;
}
.rtk-panel ::v-deep .mx-icon-calendar {
  color: #8b95a8 !important;
}

.section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.section-title {
  margin: 0;
  font-weight: 600;
  font-size: 13px;
  color: #c4cad6;
  letter-spacing: -0.2px;
}

.refresh_btn {
  padding: 0;
  width: 26px;
  height: 26px;
  min-width: 26px;
  background: #2d3548;
  color: #c4cad6;
  border: 1px solid #3d4559;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  line-height: 1;
  cursor: pointer;
  transition: all 0.15s ease;
}

.refresh_btn:hover {
  background: #3d4559;
  color: #fff;
}

.refresh_btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.equipment-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.equipment_btn {
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  text-align: left;
  padding: 10px 12px;
  background: #252b3d;
  border: 1px solid #3d4559;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.15s ease;
}

.equipment_btn:hover {
  background: #2d3548;
  border-color: #4d5a72;
}
.equipment-no-today {
  border-color: #fbbf24;
  background: #2c1b06;
}
.equipment-no-today:hover {
  border-color: #f59e0b;
  background: #342008;
}
.equipment-no-today .equipment-speed-row {
  color: #fbbf24;
}

.equipment_btn.tracking {
  border: 2px solid #3b82f6;
  background: #1e3a5f;
  box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.3);
}

.equipment_btn.tracking .equipment-name::before {
  content: "📍 ";
}

.equipment-main {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}
.equipment-meta {
  display: flex;
  align-items: center;
  gap: 6px;
}

.equipment-name {
  font-weight: 600;
  font-size: 13px;
  color: #e8ecf4;
}

.equipment-car {
  font-size: 11px;
  color: #8b95a8;
  background: #3d4559;
  padding: 2px 6px;
  border-radius: 4px;
}
.no-today-badge {
  font-size: 10px;
  font-weight: 600;
  color: #fcd34d;
  background: rgba(252, 211, 77, 0.15);
  border: 1px solid rgba(252, 211, 77, 0.4);
  padding: 2px 6px;
  border-radius: 4px;
}

.equipment-sub {
  display: flex;
  gap: 10px;
  font-size: 11px;
  color: #8b95a8;
  margin-top: 4px;
}

.equipment-stats {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-top: 8px;
  padding-top: 8px;
  border-top: 1px solid #3d4559;
}

.stat-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 11px;
}

.stat-label {
  color: #8b95a8;
}

.stat-value {
  color: #e8ecf4;
  font-weight: 500;
}

.equipment-speed-row {
  width: 100%;
  margin-top: 6px;
  padding-top: 6px;
  border-top: 1px solid #3d4559;
  font-size: 11px;
  color: #6ee7b7;
}

.placeholder {
  padding: 12px;
  background: #252b3d;
  border: 1px solid #3d4559;
  border-radius: 8px;
  font-size: 12px;
  color: #8b95a8;
  text-align: center;
}

.placeholder.error {
  background: #3d2a2a;
  border-color: #5c3a3a;
  color: #f5a8a8;
}

.equipment-actions {
  width: 100%;
  margin-top: 8px;
  padding-top: 8px;
  border-top: 1px solid #3d4559;
}

.route-btn {
  width: 100%;
  padding: 6px 10px;
  background: #3d4559;
  color: #c4cad6;
  border: 1px solid #4d5a72;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s ease;
}

.route-btn:hover {
  background: #4d5a72;
  color: #fff;
}

.route-btn.active {
  background: #2563eb;
  border-color: #3b82f6;
  color: #fff;
}

.route-btn.active:hover {
  background: #1d4ed8;
}

.playback-controls {
  display: flex;
  gap: 6px;
  margin-top: 8px;
}

.playback-btn {
  flex: 1;
  padding: 8px 10px;
  background: #252b3d;
  color: #c4cad6;
  border: 1px solid #3d4559;
  border-radius: 6px;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.15s ease;
  display: flex;
  align-items: center;
  justify-content: center;
}

.playback-btn:hover {
  background: #3d4559;
  color: #fff;
}

.playback-btn.active {
  background: #f59e0b;
  border-color: #fbbf24;
  color: #fff;
}

.playback-btn.active:hover {
  background: #d97706;
}
</style>
